/**
 * Textos (`es`) de la reimpresión de etiquetas de lote (OBR-178): namespaces `backoffice.lotLabelReprint` (diálogo e historial del
 * BackOffice) y `planta.stockLabels` (pantalla de Existencias de la App). Tramo propio: no se toca `es.ts`. Las tres lenguas deben
 * tener las mismas claves (i18n.test.ts); ningún texto literal en las vistas. Vocabulario: «Existencias», nunca «Stock».
 */

export const reprintEs = {
  'backoffice.lotLabelReprint': {
    // ── Acción y diálogo ──────────────────────────────────────────────────────────────
    action: 'Reimprimir la etiqueta',
    actionNoLot: 'Sin lote: no se puede etiquetar',
    dialogTitle: 'Reimprimir la etiqueta del lote',
    dialogBody:
      'Se genera un PDF con el mismo lote, la misma caducidad y el mismo QR de la etiqueta original. No cambia las existencias ni crea ningún lote nuevo: solo queda constancia (quién, cuándo, cuántas copias y por qué).',
    noLot:
      'Estas existencias no tienen lote, así que no se pueden etiquetar. Para darles un lote, regularízalas desde «A regularizar».',
    fieldArticle: 'Artículo',
    fieldLot: 'Lote',
    fieldExpiry: 'Caducidad',
    fieldPlace: 'Lugar',
    fieldQuantity: 'Existencias',
    noPlace: 'Sin lugar',
    noExpiry: 'Sin caducidad',
    copiesLabel: 'Copias',
    copiesHint: 'De 1 a 99 copias.',
    copiesInvalid: 'Indica un número de copias entre 1 y 99.',
    reasonLabel: 'Motivo',
    reasonPlaceholder: 'Elige un motivo',
    reasonRequired: 'Elige por qué la reimprimes.',
    reason_not_labelled: 'No se etiquetó',
    reason_damaged: 'Etiqueta dañada',
    reason_other: 'Otros',
    noteLabel: 'Nota',
    noteOptionalHint: 'Opcional.',
    noteRequiredHint: 'Obligatoria cuando el motivo es «Otros».',
    noteRequired: 'Escribe el motivo de la reimpresión.',
    submit: 'Registrar y generar el PDF',
    submitting: 'Generando…',
    retryRecord: 'Volver a registrar',
    retryDownload: 'Volver a descargar el PDF',
    downloadAgain: 'Descargar el PDF otra vez',
    close: 'Cerrar',
    cancel: 'Cancelar',

    // ── Resultado ─────────────────────────────────────────────────────────────────────
    doneTitle: 'Etiqueta reimpresa',
    doneBody_one: 'Se ha registrado la reimpresión y se ha descargado el PDF con {{count}} copia.',
    doneBody_other: 'Se ha registrado la reimpresión y se ha descargado el PDF con {{count}} copias.',
    errPdf:
      'No se ha podido generar el PDF de la etiqueta. No se ha registrado nada ni se ha descargado ninguna etiqueta: vuelve a intentarlo.',
    errRecordAfterPdf:
      'El PDF está hecho, pero NO se ha podido registrar la reimpresión, y por eso no se ha descargado ninguna etiqueta. Vuelve a intentarlo: se registrará una sola vez.',
    errDownload:
      'La reimpresión se ha registrado, pero el navegador no ha podido descargar el PDF. Vuelve a intentarlo: no se volverá a registrar.',

    // ── Historial del lote ────────────────────────────────────────────────────────────
    historyTitle: 'Reimpresiones de este lote',
    historyShow: 'Mostrar las reimpresiones',
    historyHide: 'Ocultar las reimpresiones',
    historyLoading: 'Cargando las reimpresiones…',
    historyEmpty: 'Este lote no se ha reimpreso nunca.',
    historyError: 'No se ha podido cargar el historial de reimpresiones.',
    historyRetry: 'Vuelve a intentarlo',
    historyCopies_one: '{{count}} copia',
    historyCopies_other: '{{count}} copias',
    historyBy: 'Por {{name}}',
    historyByUnknown: 'Por una persona del obrador',
    historyExpiry: 'Caducidad en la etiqueta: {{date}}',
    historyNoExpiry: 'Sin caducidad en la etiqueta',
    historyOrigin_backoffice: 'Desde el BackOffice',
    historyOrigin_app: 'Desde la App',
    historyPlace: 'Lugar: {{place}}',
    historyNoPlace: 'Sin lugar',
    historyMore: 'Se muestran las {{count}} reimpresiones más recientes.',

    errQr:
      'No se ha podido generar el código QR de la etiqueta, y una reimpresión no se hace sin el mismo QR. No se ha registrado nada ni se ha descargado ninguna etiqueta: vuelve a intentarlo.',
    errQrNoRoom:
      'El formato de etiqueta no tiene sitio para el QR (demasiado texto para el tamaño de la etiqueta). No se ha registrado nada ni se ha descargado ninguna etiqueta: revisa el formato en Configuración.',
    noticeExpiryChanged:
      'La caducidad del lote ha cambiado desde que se cargó la pantalla: la etiqueta lleva la vigente ({{date}}).',
    noticeExpiryRemoved:
      'El lote ya no tiene caducidad desde que se cargó la pantalla: la etiqueta no lleva ninguna.',
    formatLoading: 'Cargando el formato de etiqueta…',
    formatWarning:
      'No se ha podido cargar el formato de etiqueta de Configuración: la reimpresión se hará con el formato sencillo.',
    formatNone:
      'Configuración no tiene ningún formato de «lote interno»: la reimpresión se hará con el formato sencillo.',
    logoWarning: 'No se ha podido cargar el logotipo del obrador: la etiqueta saldrá sin logotipo.',
    historyLotExpiryWas: 'el lote tenía {{date}}',
    historyLotNoExpiryWas: 'el lote no tenía caducidad',

    // ── Errores de la RPC (nunca crudos) ──────────────────────────────────────────────
    errForbidden: 'No tienes permiso para registrar reimpresiones en este obrador.',
    errNoteTooLong: 'La nota es demasiado larga: 500 caracteres como máximo.',
    errBadCopies: 'El número de copias debe estar entre 1 y 99.',
    errBadReason: 'Elige el motivo de la reimpresión.',
    errNoteRequired: 'Cuando el motivo es «Otros» hay que escribir una nota.',
    errLotRequired: 'Estas existencias no tienen lote: no se pueden etiquetar.',
    errUnknownLot: 'No encontramos este lote en el obrador. Vuelve a cargar Existencias.',
    errBadPlace: 'El lugar de estas existencias ya no es de este obrador. Vuelve a cargar Existencias.',
    errGeneric: 'No se ha podido registrar la reimpresión. Vuelve a intentarlo.',
  },

  'planta.stockLabels': {
    // ── Entrada (portada de Recepción) y pantalla ─────────────────────────────────────
    open: 'Reimprimir la etiqueta de un lote',
    title: 'Existencias',
    subtitle: 'Busca un lote para reimprimir su etiqueta.',
    searchLabel: 'Busca un lote',
    searchPlaceholder: 'Artículo, lote o ubicación',
    searchClear: 'Borrar la búsqueda',
    emptyNoData: 'Todavía no hay lotes en el dispositivo. Conéctalo a la red para que se descarguen.',
    emptyNoMatch: 'Ningún lote coincide con «{{query}}».',
    emptyWeb: 'Esta pantalla solo funciona en la App de planta, no en la versión web.',
    resultsCount_one: '{{count}} lote',
    resultsCount_other: '{{count}} lotes',
    resultsLimited: 'Se muestran los primeros {{count}}. Afina la búsqueda para ver más.',
    truncated:
      'La lista se limita a los {{count}} primeros lotes. Afina la búsqueda o consulta Existencias en el BackOffice.',

    refresh: 'Actualizar la lista',
    refreshing: 'Actualizando la lista…',
    refreshFailed:
      'No se ha podido actualizar la lista. Se muestra la última copia que había en el dispositivo.',
    refreshOffline: 'Sin conexión: se muestra la última copia que había en el dispositivo.',

    // ── Un lote (línea lote + lugar) ──────────────────────────────────────────────────
    lotLine: 'Lote {{lot}}',
    expiresOn: 'Caduca {{date}}',
    noExpiry: 'Sin caducidad',
    placeLine: 'Lugar: {{place}}',
    noPlace: 'Sin lugar',
    quantityLine: 'Existencias: {{quantity}}',
    chooseLot: 'Reimprimir',

    // ── Reimprimir ────────────────────────────────────────────────────────────────────
    printTitle: 'Reimprimir la etiqueta',
    reasonTitle: '¿Por qué la reimprimes?',
    reason_not_labelled: 'No se etiquetó',
    reason_damaged: 'Etiqueta dañada',
    reason_other: 'Otros',
    reasonRequired: 'Elige un motivo para poder imprimir.',
    noteLabel: 'Explica el motivo',
    notePlaceholder: 'Escríbelo aquí',
    noteRequired: 'Escribe el motivo para poder imprimir.',
    printCopies_one: 'Imprimir {{count}} etiqueta',
    printCopies_other: 'Imprimir {{count}} etiquetas',
    printing: 'Imprimiendo…',
    back: 'Atrás',
    printedTo:
      'Etiqueta enviada a {{printer}}. La reimpresión queda registrada: si no hay conexión, se enviará al recuperarla.',
    printedNotRecorded:
      'Etiqueta enviada a {{printer}}, pero no se ha podido guardar el registro de la reimpresión en este dispositivo. Avisa a un responsable.',
    printLeftTitle: 'No se ha impreso nada, así que no se ha registrado nada.',
  },
} as const;
